import { useRef, useState } from "react";
import { Plus, Trash2, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fileUrl } from "@/lib/api";
import { useAdminApi } from "@/admin/useAdminApi";
import Markdown from "@/components/site/Markdown";

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function ImageUpload({ value, onChange, testId, asMarkdown }) {
  const call = useAdminApi();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await call("post", "/admin/upload", fd);
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-3">
      {!asMarkdown && value && <img src={fileUrl(value)} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-[#DCE5E8] object-contain bg-white" />}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      <button type="button" className="btn-secondary btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testId}-upload`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {asMarkdown ? "Insert image" : "Upload image"}
      </button>
    </div>
  );
}

function Chips({ value = [], onChange, options, max, testId }) {
  const left = options.filter((o) => !value.includes(o.value));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((v) => (
          <span key={v} className="chip bg-[#EDF2F4] text-[#17243A] gap-1">{options.find((o) => o.value === v)?.label || v}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} aria-label="Remove"><X className="h-3 w-3" /></button></span>
        ))}
        {!value.length && <span className="text-xs text-[#8A96A8]">None selected</span>}
      </div>
      {(!max || value.length < max) && (
        <select className="input" value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} data-testid={testId}>
          <option value="">Add…</option>
          {left.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </div>
  );
}

function Repeater({ value = [], onChange, blank, render, testId, addLabel }) {
  const set = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-3">
      {value.map((row, i) => (
        <div key={i} className="flex gap-2 items-start rounded-xl border border-[#DCE5E8] p-3 bg-[#F1F4F6]">
          <div className="flex-1 space-y-2">{render(row, (v) => set(i, v), i)}</div>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="btn-ghost !p-2 text-rose-600" aria-label="Remove row" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" onClick={() => onChange([...value, blank()])} data-testid={`${testId}-add`}><Plus className="h-3.5 w-3.5" />{addLabel}</button>
    </div>
  );
}

const WRAP = [["B", "**", "**"], ["I", "_", "_"], ["H2", "\n## ", ""], ["H3", "\n### ", ""], ["List", "\n- ", ""], ["Quote", "\n> ", ""]];

function MarkdownField({ id, f, value, set, ctx }) {
  const [preview, setPreview] = useState(false);
  const ref = useRef();
  const insert = (pre, post = "", fallback = "text") => {
    const el = ref.current, v = value || "";
    const [a, b] = el ? [el.selectionStart, el.selectionEnd] : [v.length, v.length];
    set(v.slice(0, a) + pre + (v.slice(a, b) || fallback) + post + v.slice(b));
  };
  const links = [...ctx.software.filter((s) => s.status !== "draft").map((s) => [`/software/${s.slug}`, s.name]), ...ctx.comparisons.filter((c) => c.status !== "draft").map((c) => [`/comparisons/${c.slug}`, c.title])];
  return (
    <div className="rounded-xl border border-[#CBD6DB] overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-[#E7EDEF] bg-[#F6F7F9]">
        {WRAP.map(([l, a, b]) => <button key={l} type="button" disabled={preview} onClick={() => insert(a, b)} className="btn-ghost btn-sm !px-2" data-testid={`${id}-tool-${l.toLowerCase()}`}>{l}</button>)}
        <select className="input !w-auto !py-1 !text-xs" value="" disabled={preview} onChange={(e) => { const l = links.find((x) => x[0] === e.target.value); if (l) insert("[", `](${l[0]})`, l[1]); }} data-testid={`${id}-internal-link`}>
          <option value="">Insert internal link…</option>
          {links.map(([u, t]) => <option key={u} value={u}>{t}</option>)}
        </select>
        {f.upload && <ImageUpload testId={`${id}-img`} asMarkdown onChange={(url) => set(`${value || ""}\n\n![Image description](${url})\n`)} />}
        <button type="button" onClick={() => setPreview(!preview)} className="ml-auto btn-secondary btn-sm" data-testid={`${id}-preview-toggle`}>{preview ? "Edit" : "Preview"}</button>
      </div>
      {preview ? <div className="p-4 max-h-[600px] overflow-y-auto"><Markdown>{value}</Markdown></div>
        : <textarea ref={ref} id={id} rows={f.rows || 8} value={value || ""} onChange={(e) => set(e.target.value)} className="w-full px-3.5 py-2.5 font-mono text-[13px] text-[#17243A] outline-none" data-testid={id} />}
    </div>
  );
}

export default function FieldInput({ f, value, set, values, ctx }) {
  const id = `field-${f.k.replace(/_/g, "-")}`;
  const swOpts = ctx.software.map((s) => ({ value: s.slug, label: s.name }));
  const swName = (slug) => ctx.software.find((s) => s.slug === slug)?.name || slug;
  switch (f.type) {
    case "textarea":
      return <textarea id={id} rows={f.rows || 3} value={value || ""} onChange={(e) => set(e.target.value)} className="input" data-testid={id} />;
    case "markdown":
      return <MarkdownField id={id} f={f} value={value} set={set} ctx={ctx} />;
    case "author":
      return (
        <select id={id} value={value || ""} onChange={(e) => set(e.target.value)} className="input" data-testid={id}>
          {[...new Set([value, ...ctx.authors.map((a) => a.name)].filter(Boolean))].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      );
    case "lines":
      return <textarea id={id} rows={4} value={(value || []).join("\n")} onChange={(e) => set(e.target.value.split("\n"))} className="input" placeholder="One item per line" data-testid={id} />;
    case "select": case "category":
      return (
        <select id={id} value={value ?? ""} onChange={(e) => set(e.target.value)} className="input" data-testid={id}>
          {f.type === "category" ? [["", "Select a category"], ...ctx.categories.map((c) => [c.slug, c.name])].map(([v, l]) => <option key={v} value={v}>{l}</option>)
            : f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      );
    case "number":
      return <input id={id} type="number" value={value ?? 0} onChange={(e) => set(Number(e.target.value))} className="input" data-testid={id} />;
    case "date":
      return <input id={id} type="date" value={(value || "").slice(0, 10)} onChange={(e) => set(e.target.value)} className="input !w-auto" data-testid={id} />;
    case "checkbox":
      return <label className="flex items-center gap-2.5 text-sm text-[#17243A] cursor-pointer"><input id={id} type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} className="h-4 w-4 accent-[#0F766E]" data-testid={id} />{f.label}</label>;
    case "checks":
      return (
        <div className="flex flex-wrap gap-2" data-testid={id}>
          {f.options.map((o) => { const on = (value || []).includes(o); return (
            <button type="button" key={o} onClick={() => set(on ? value.filter((x) => x !== o) : [...(value || []), o])}
              className={`btn btn-sm border ${on ? "bg-[#EDF2F4] border-[#3F5E7A] text-[#3F5E7A]" : "bg-white border-[#CBD6DB] text-[#526174]"}`}>{o}</button>); })}
        </div>
      );
    case "image":
      return <div className="space-y-2"><input id={id} value={value || ""} onChange={(e) => set(e.target.value)} className="input" placeholder="https://… or upload" data-testid={id} /><ImageUpload value={value} onChange={set} testId={id} /></div>;
    case "software":
      return <Chips value={value} onChange={set} options={swOpts.filter((o) => o.value !== values.slug)} max={f.max} testId={id} />;
    case "sources":
      return <Repeater value={value} onChange={set} testId={id} addLabel="Add source" blank={() => ({ title: "", url: "" })} render={(r, upd, i) => (<>
        <input value={r.title} onChange={(e) => upd({ ...r, title: e.target.value })} placeholder="Title (e.g. Official pricing page)" className="input" data-testid={`${id}-title-${i}`} />
        <input value={r.url} onChange={(e) => upd({ ...r, url: e.target.value })} placeholder="https://…" className="input" data-testid={`${id}-url-${i}`} /></>)} />;
    case "screenshots":
      return <Repeater value={value} onChange={set} testId={id} addLabel="Add screenshot" blank={() => ({ url: "", caption: "", credit: "" })} render={(r, upd, i) => (<>
        <div className="flex gap-2 items-center"><input value={r.url} onChange={(e) => upd({ ...r, url: e.target.value })} placeholder="Image URL or upload" className="input" data-testid={`${id}-url-${i}`} />
          <ImageUpload value={r.url} onChange={(u) => upd({ ...r, url: u })} testId={`${id}-shot`} /></div>
        <input value={r.caption} onChange={(e) => upd({ ...r, caption: e.target.value })} placeholder="Caption (what the screenshot shows)" className="input" data-testid={`${id}-caption-${i}`} />
        <input value={r.credit} onChange={(e) => upd({ ...r, credit: e.target.value })} placeholder="Source / credit (e.g. Screenshot by SaaSPivora, or Vendor press kit)" className="input" data-testid={`${id}-credit-${i}`} /></>)} />;
    case "faqs":
      return <Repeater value={value} onChange={set} testId={id} addLabel="Add FAQ" blank={() => ({ q: "", a: "" })} render={(r, upd) => (<>
        <input value={r.q} onChange={(e) => upd({ ...r, q: e.target.value })} placeholder="Question" className="input" />
        <textarea value={r.a} onChange={(e) => upd({ ...r, a: e.target.value })} placeholder="Answer" rows={2} className="input" /></>)} />;
    case "feature-rows": {
      const sw = values.software || [];
      if (sw.length < 2) return <p className="text-sm text-[#64728A]">Select at least two software products above first.</p>;
      return <Repeater value={value} onChange={set} testId={id} addLabel="Add feature row" blank={() => ({ feature: "", values: sw.map(() => "") })} render={(r, upd) => (<>
        <input value={r.feature} onChange={(e) => upd({ ...r, feature: e.target.value })} placeholder="Feature (e.g. Free plan)" className="input font-semibold" />
        <div className="grid sm:grid-cols-2 gap-2">{sw.map((s, i) => (
          <input key={s} value={r.values[i] || ""} placeholder={swName(s)} className="input"
            onChange={(e) => { const v = [...r.values]; v[i] = e.target.value; upd({ ...r, values: v }); }} />))}</div></>)} />;
    }
    case "compare-items": {
      const sw = values.software || [];
      if (!sw.length) return <p className="text-sm text-[#64728A]">Select software above first.</p>;
      const items = sw.map((s) => (value || []).find((i) => i.slug === s) || { slug: s, pricing: "", ideal_customer: "", strengths: [], weaknesses: [] });
      const upd = (i, patch) => set(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
      return (
        <div className="grid md:grid-cols-2 gap-4" data-testid={id}>
          {items.map((it, i) => (
            <div key={it.slug} className="rounded-xl border border-[#DCE5E8] p-4 space-y-2 bg-[#F1F4F6]">
              <p className="font-semibold text-[#17243A]">{swName(it.slug)}</p>
              <textarea rows={2} value={it.pricing} onChange={(e) => upd(i, { pricing: e.target.value })} placeholder="Pricing notes (verified facts only)" className="input" />
              <textarea rows={2} value={it.ideal_customer} onChange={(e) => upd(i, { ideal_customer: e.target.value })} placeholder="Ideal customer profile" className="input" />
              <textarea rows={3} value={it.strengths.join("\n")} onChange={(e) => upd(i, { strengths: e.target.value.split("\n") })} placeholder="Strengths (one per line)" className="input" />
              <textarea rows={3} value={it.weaknesses.join("\n")} onChange={(e) => upd(i, { weaknesses: e.target.value.split("\n") })} placeholder="Weaknesses (one per line)" className="input" />
            </div>
          ))}
        </div>
      );
    }
    case "publish":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label" htmlFor="field-status">Status</label>
            <select id="field-status" value={values.status || "draft"} onChange={(e) => ctx.patch({ status: e.target.value })} className="input" data-testid="field-status">
              <option value="draft">Draft (not public)</option><option value="published">Published</option><option value="scheduled">Scheduled</option>
            </select></div>
          {values.status === "scheduled" && <div><label className="label" htmlFor="field-publish-at">Publish at</label>
            <input id="field-publish-at" type="datetime-local" value={toLocal(values.publish_at)} onChange={(e) => ctx.patch({ publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="input" data-testid="field-publish-at" /></div>}
        </div>
      );
    default:
      return <input id={id} value={value ?? ""} onChange={(e) => set(e.target.value)} className="input" required={f.required} data-testid={id} />;
  }
}
